'use client';

import { motion } from 'motion/react';
import { Mail, Phone, MapPin, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { ThankYouModal } from './ThankYouModal';
import { trackFormConversion, getUTMParameters } from '../utils/analytics';
import { detectUserCountry } from '../utils/countryDetection';
import countries from 'world-countries';
import Image from 'next/image';

export function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [phone, setPhone] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('AE');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  // Function to get flag emoji from country code
  const getFlagEmoji = (countryCode: string) => {
    return countryCode
      .toUpperCase()
      .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));
  };

  // Prepare country options with calling codes
  const countryOptions = countries
    .filter(country => country.idd.root)
    .map(country => ({
      code: country.cca2,
      name: country.name.common,
      dialCode: `${country.idd.root}${country.idd.suffixes?.[0] || ''}`,
      flag: getFlagEmoji(country.cca2)
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter countries based on search
  const filteredCountries = countryOptions.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.dialCode.includes(searchQuery)
  );

  // Get selected country details
  const selectedCountry = countryOptions.find(c => c.code === selectedCountryCode);

  // Detect user's country using IP detection with fallbacks
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const detectedCountry = await detectUserCountry();
        setSelectedCountryCode(detectedCountry);
      } catch (error) {
        console.log('Country detection failed, using default');
        setSelectedCountryCode('AE');
      }
    };
    detectCountry();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const utmParams = getUTMParameters();
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', `${selectedCountry?.dialCode} ${phone}`);
      formDataToSend.append('country', selectedCountry?.name || '');
      formDataToSend.append('formSource', 'Contact Section');
      formDataToSend.append('utm_id', 'LP04');
      formDataToSend.append('utm_source', 'organic_google');
      formDataToSend.append('campaign_name', 'Gate Eleven Landing Page');
      formDataToSend.append('media_source', 'Gate Eleven Residences Website');
      formDataToSend.append('method_of_contact', 'website');
      formDataToSend.append('property_ref_no.', '8478');

      const response = await fetch('https://hooks.zapier.com/hooks/catch/18859173/uwfr4km/', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        // Track conversion with enhanced data
        trackFormConversion('Contact Section', formData.email, `${selectedCountry?.dialCode}${phone}`);
        // Reset form
        setFormData({ firstName: '', lastName: '', email: '' });
        setPhone('');
        // Show thank you modal
        setShowThankYou(true);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section id="contact" className="bg-[#c19964]/10 py-24 md:py-36">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-[1px] bg-black w-[80px] mx-auto mb-8 origin-center"
          />
          <h2 className="text-black tracking-[0.15em] font-light">CONNECT WITH US</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 max-w-6xl mx-auto">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    className="w-full px-6 py-4 bg-transparent border border-black/20 text-black text-base placeholder:text-black/40 focus:outline-none focus:border-black/60 transition-colors tracking-wider"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                    className="w-full px-6 py-4 bg-transparent border border-black/20 text-black text-base placeholder:text-black/40 focus:outline-none focus:border-black/60 transition-colors tracking-wider"
                  />
                </div>
              </div>

              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-transparent border border-black/20 text-black text-base placeholder:text-black/40 focus:outline-none focus:border-black/60 transition-colors tracking-wider"
                />
              </div>

              <div className="flex gap-2">
                {/* Custom Searchable Country Dropdown */}
                <div ref={dropdownRef} className="relative min-w-[140px]">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full bg-transparent border border-black/20 text-black px-4 py-4 text-base focus:outline-none focus:border-black/60 transition-all flex items-center justify-between"
                  >
                    <span>{selectedCountry?.flag} {selectedCountry?.dialCode}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-black/30 max-h-60 overflow-hidden z-50 shadow-xl">
                      <div className="p-2 border-b border-black/20">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-transparent border border-black/20 text-black placeholder:text-black/40 px-3 py-2 text-sm focus:outline-none focus:border-black/40"
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredCountries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setSelectedCountryCode(country.code);
                              setIsDropdownOpen(false);
                              setSearchQuery('');
                            }}
                            className="w-full px-3 py-2 text-left text-black hover:bg-black/5 transition-colors flex items-center gap-2 text-sm"
                          >
                            <span>{country.flag}</span>
                            <span className="text-black/60">{country.dialCode}</span>
                          </button>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="px-3 py-4 text-black/40 text-sm text-center">
                            No countries found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <input
                  type="tel"
                  placeholder="Mobile Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="flex-1 px-3 py-3 w-full bg-transparent border border-black/20 text-black text-base placeholder:text-black/40 focus:outline-none focus:border-black/60 transition-colors tracking-wider"
                />
              </div>

              {/* Terms and Privacy Notice */}
              <div className="text-center mt-4 mb-2">
                <p className="text-black/60 text-xs">
                  By clicking Submit, you agree to our{' '}
                  <a href="/terms-and-conditions" className="text-[#c19964] hover:text-[#a88555] underline transition-colors">
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy-policy" className="text-[#c19964] hover:text-[#a88555] underline transition-colors">
                    Privacy Policy
                  </a>
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#c19964] text-white px-8 py-4 hover:bg-[#c19964]/90 transition-colors tracking-[0.2em] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-8">
              {[
                {
                  icon: Phone,
                  content: '+971 58 898 0456',
                },
                {
                  icon: Mail,
                  content: 'info@zedcapital.ae',
                },
                {
                  icon: MapPin,
                  content: 'Dubai, UAE',
                },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="flex items-center gap-4 pb-6 border-b border-black/10"
                  >
                    <Icon className="w-5 h-5 text-[#c19964]/60" />
                    <div className="text-black tracking-wider font-light">
                      {item.content}
                    </div>
                  </motion.div>
                );
              })}
            </div>
             {/* QR Code */}
            <div className="flex flex-col justify-center items-end pt-4">
              <Image
                src="/images/qr-gate.jpeg"
                alt="QR Code - Gate Eleven Residences"
                width={120}
                height={120}
                className="object-contain"
              />
              <p className="text-black text-sm lg:text-md mt-2 text-right">DLD Permit No.:</p>
              <p className="text-black text-sm lg:text-md text-right">128450</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Thank You Modal */}
      <ThankYouModal isOpen={showThankYou} onClose={() => setShowThankYou(false)} />
    </section>
  );
}